import React from "react";

export default function ProductOptionsSection({
  optionGroups,
  addOptionGroup,
  addReusableOptionGroup,
  removeOptionGroup,
  updateOptionGroup,
  addOptionValue,
  removeOptionValue,
  updateOptionValue,
  reusableGroups = [],
  loadingReusableGroups = false,
  reusableGroupsError = "",
  selectedReusableGroupId = "",
  setSelectedReusableGroupId,
  isEditMode = false,
}) {
  return (
    <>
      <div className="md:col-span-2">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Item Options
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              Reusable groups can be selected below.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-[240px]">
              <select
                value={selectedReusableGroupId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedReusableGroupId(value);

                  if (value) {
                    addReusableOptionGroup(value);
                  }
                }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
                disabled={loadingReusableGroups}
              >
                <option value="">Select existing group</option>
                {reusableGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={addOptionGroup}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Add New Group
            </button>
          </div>
        </div>

        {loadingReusableGroups && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            Loading reusable option groups...
          </div>
        )}

        {reusableGroupsError && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {reusableGroupsError}
          </div>
        )}
      </div>

      <div className="space-y-3 md:col-span-2">
        {optionGroups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
            No option categories added yet.
          </div>
        ) : (
          optionGroups.map((group, groupIndex) => {
            const isLockedReusableGroup = group.is_reusable && !isEditMode;

            return (
              <div
                key={group.id || group.temp_id || group.reusable_group_id}
                className="rounded-xl border border-slate-200 p-3"
              >
               
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Category Name
                      </label>
                      <input
                        type="text"
                        value={group.name}
                        onChange={(e) =>
                          updateOptionGroup(groupIndex, "name", e.target.value)
                        }
                        disabled={isLockedReusableGroup}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-900 disabled:cursor-not-allowed disabled:bg-slate-100"
                        placeholder="e.g. Size"
                      />
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center md:justify-end">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={group.is_required}
                          onChange={(e) =>
                            updateOptionGroup(
                              groupIndex,
                              "is_required",
                              e.target.checked
                            )
                          }
                          disabled={isLockedReusableGroup}
                          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                        <span className="text-sm text-slate-700">Required</span>
                      </label>

                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={group.is_active}
                          onChange={(e) =>
                            updateOptionGroup(
                              groupIndex,
                              "is_active",
                              e.target.checked
                            )
                          }
                          disabled={isLockedReusableGroup}
                          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                        <span className="text-sm text-slate-700">Active</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => removeOptionGroup(groupIndex)}
                        className="self-start rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 sm:self-auto"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {group.values.map((value, valueIndex) => (
                    <div
                      key={value.id || value.temp_id || value.reusable_value_id}
                      className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center"
                    >
                      <div className="flex flex-1 items-center gap-2">
                        <input
                          type="text"
                          value={value.label}
                          onChange={(e) =>
                            updateOptionValue(
                              groupIndex,
                              valueIndex,
                              "label",
                              e.target.value
                            )
                          }
                          disabled={isLockedReusableGroup}
                          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900 disabled:cursor-not-allowed disabled:bg-slate-100"
                          placeholder="e.g. Large"
                        />

                        <input
                          type="number"
                          step="0.01"
                          value={value.price}
                          onChange={(e) =>
                            updateOptionValue(
                              groupIndex,
                              valueIndex,
                              "price",
                              e.target.value
                            )
                          }
                          disabled={isLockedReusableGroup}
                          className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900 disabled:cursor-not-allowed disabled:bg-slate-100"
                          placeholder="0.00"
                        />
                      </div>

                      <label className="flex items-center gap-2 text-sm text-slate-700 sm:shrink-0">
                        <input
                          type="radio"
                          name={`default_option_group_${groupIndex}`}
                          checked={value.is_default}
                          onChange={(e) =>
                            updateOptionValue(
                              groupIndex,
                              valueIndex,
                              "is_default",
                              e.target.checked
                            )
                          }
                          disabled={isLockedReusableGroup}
                          className="h-4 w-4 border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                        Default
                      </label>

                      <button
                        type="button"
                        onClick={() => removeOptionValue(groupIndex, valueIndex)}
                        disabled={isLockedReusableGroup}
                        className="text-left text-sm font-medium text-red-600 transition hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => addOptionValue(groupIndex)}
                    disabled={isLockedReusableGroup}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Option Value
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}